import type React from 'react';
import { HashLink as LinkUntyped } from 'react-router-hash-link';
import classnames from 'classnames';

import { Tooltip } from 'antd';
import ButtonLoading from '@/shared/ui/components/Loadings/buttonLoading';
import useIconButton from './useIconButton';

// HashLinkProps has no `disabled` (it renders an <a>, which doesn't support
// it either) — pre-existing prop passthrough, kept as-is.
const Link = LinkUntyped as any;

export type IconButtonProps = {
  id?: string;
  className?: string;
  isFilledGradient?: boolean;
  isFilled?: boolean;
  isOutline?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  iconComponent: React.ReactNode;
  iconText?: string;
  isIconLinkButton?: boolean;
  link?: string;
  isIconWithText?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  target?: string;
  isLoading?: boolean;
  isDebounceClick?: boolean;
  tooltip?: string;
  isOpposite?: boolean;
  isFilledIconWithText?: boolean;
  ariaLabel?: string;
};

const IconButton = ({
  id = '',
  className = '',
  isFilledGradient = false,
  isFilled = false,
  isOutline = false,
  disabled = false,
  style = {},
  textStyle = {},
  iconComponent,
  iconText = '',
  isIconLinkButton = false,
  link = '',
  isIconWithText = false,
  onClick = () => {},
  target,
  isLoading = false,
  isDebounceClick = false,
  tooltip = '',
  isOpposite,
  isFilledIconWithText,
  ariaLabel,
}: IconButtonProps) => {
  const { handleClick } = useIconButton({ onClick, isDebounceClick });

  return isIconLinkButton ? (
    <Link
      id={id}
      aria-label={ariaLabel || tooltip || undefined}
      className={classnames(
        'icon-btn',
        isFilledGradient
          ? '--filled-gradient'
          : isFilled
            ? '--filled'
            : isOutline
              ? '--outline'
              : isIconWithText
                ? '--icon-with-text'
                : '--icon-only',
        className,
        isOpposite ? '--opposite' : '',
      )}
      style={style}
      disabled={disabled}
      to={link}
      target={target}
    >
      {isOpposite ? (
        <>
          {!!iconText && (
            <span className="icon-text" style={textStyle}>
              {iconText}
            </span>
          )}

          {iconComponent}
        </>
      ) : (
        <>
          {iconComponent}

          {!!iconText && (
            <span className="icon-text" style={textStyle}>
              {iconText}
            </span>
          )}
        </>
      )}
    </Link>
  ) : (
    <button
      id={id}
      aria-label={ariaLabel || tooltip || undefined}
      className={classnames(
        'icon-btn',
        isFilledGradient
          ? classnames('--filled-gradient', isLoading ? '--is-loading' : '')
          : isFilled
            ? '--filled'
            : isOutline
              ? '--outline'
              : isFilledIconWithText
                ? '--filled-icon-with-text'
                : isIconWithText
                  ? '--icon-with-text'
                  : '--icon-only',
        className,
        isOpposite ? '--opposite' : '',
      )}
      type="button"
      style={style}
      disabled={disabled}
      onClick={isLoading ? undefined : handleClick}
    >
      <Tooltip placement="bottom" title={tooltip}>
        {isOpposite ? (
          <>
            {!!iconText && (
              <span className="icon-text" style={textStyle}>
                {iconText}
              </span>
            )}

            {!isLoading && iconComponent}
          </>
        ) : (
          <>
            {!isLoading && iconComponent}

            {!!iconText && (
              <span className="icon-text" style={textStyle}>
                {iconText}
              </span>
            )}
          </>
        )}
      </Tooltip>
      {isLoading && <ButtonLoading isFilled={isFilledGradient} />}
    </button>
  );
};

export default IconButton;
